import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getHeroVideos } from "@/utils/video";
import { VIDEO_TYPE_LABEL } from "@/types/video";

export default function HeroCarousel() {
  const slides = getHeroVideos();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  const go = useCallback(
    (next: number) => {
      if (slides.length === 0) return;
      setIndex((next + slides.length) % slides.length);
    },
    [slides.length],
  );

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % slides.length), 5000);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  if (slides.length === 0) return null;

  return (
    <section
      className="relative -mt-16 h-[460px] w-full overflow-hidden bg-background-900 md:-mt-[72px] md:h-[580px] lg:h-[640px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={slide.backdrop}
            alt={slide.video.title}
            title={`${slide.video.title} - 高清影视在线观看`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/75 to-background-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/60"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full items-end px-4 pb-14 md:px-6 md:pb-16 lg:px-10 lg:pb-20">
        <div className="w-full max-w-2xl">
          <div key={index} className="animate-fade-up">
            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs md:text-sm">
              <span className="rounded-md bg-primary-500 px-2.5 py-1 font-medium text-background-950">
                {VIDEO_TYPE_LABEL[slides[index].video.type]}
              </span>
              <span className="flex items-center gap-1 rounded-md bg-background-800/80 px-2.5 py-1 text-accent-400">
                <i className="ri-star-fill text-[10px]"></i>
                {slides[index].video.rating.toFixed(1)}
              </span>
              <span className="text-foreground-300">
                {slides[index].video.year} · {slides[index].video.region}
              </span>
              <span className="text-foreground-400">{slides[index].video.genres.join(" / ")}</span>
            </div>
            <h1 className="text-3xl font-bold leading-tight text-foreground-50 md:text-5xl">
              {slides[index].video.title}
            </h1>
            <p className="clamp-2 mt-4 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
              {slides[index].video.summary}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(`/detail/${slides[index].video.id}`)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-play-fill text-lg"></i>
                立即观看
              </button>
              <button
                type="button"
                onClick={() => navigate(`/category/${slides[index].video.type}`)}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 bg-background-900/70 px-6 py-3 text-sm font-medium text-foreground-100 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                <i className="ri-bookmark-line text-base"></i>
                查看分类
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 right-4 z-20 flex items-center gap-2 md:bottom-8 md:right-10">
        <button
          type="button"
          aria-label="上一个"
          onClick={() => go(index - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 bg-background-900/70 text-foreground-100 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          type="button"
          aria-label="下一个"
          onClick={() => go(index + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 bg-background-900/70 text-foreground-100 backdrop-blur transition-colors hover:border-primary-500 hover:text-primary-400 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>

      <div className="absolute bottom-6 left-4 z-20 flex items-center gap-2 md:bottom-9 md:left-10">
        {slides.map((slide, i) => (
          <button
            key={slide.video.id}
            type="button"
            aria-label={`切换到 ${slide.video.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-foreground-600 hover:bg-foreground-400"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}